import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

const initialState = {
  sales_order_approver_list: [],
  sales_order_approver_detail: null,
  current_values: null,
  active_payload: null,
  loading: false,
  error: null,
};

// Get all Sales Order Approvers
export const getSalesOrderApprovers = createAsyncThunk(
  "salesOrderApprover/getAll",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(`${API_URL.APPROVER.SALES_ORDER.SLUG}?${queryString}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Get Sales Order Approver by ID
export const getSalesOrderApproverById = createAsyncThunk(
  "salesOrderApprover/getById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.APPROVER.SALES_ORDER.SLUG}/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Create Sales Order Approver
export const createSalesOrderApprover = createAsyncThunk(
  "salesOrderApprover/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.APPROVER.SALES_ORDER.SLUG, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Update Sales Order Approver
export const updateSalesOrderApprover = createAsyncThunk(
  "salesOrderApprover/update",
  async ({ id, data: payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL.APPROVER.SALES_ORDER.SLUG}/${id}`, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Delete Sales Order Approver
export const deleteSalesOrderApprover = createAsyncThunk(
  "salesOrderApprover/delete",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.APPROVER.SALES_ORDER.SLUG}/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const salesOrderApproverSlice = createSlice({
  name: "salesOrderApprover",
  initialState,
  reducers: {
    setCurrentValues: (state, action) => {
      state.current_values = action.payload;
    },
    clearCurrentValues: (state) => {
      state.current_values = null;
    },
    setActivePayload: (state, action) => {
      state.active_payload = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all Sales Order Approvers
      .addCase(getSalesOrderApprovers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSalesOrderApprovers.fulfilled, (state, action) => {
        state.loading = false;
        state.sales_order_approver_list = action.payload?.data?.list || [];
      })
      .addCase(getSalesOrderApprovers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Sales Order Approver by ID
      .addCase(getSalesOrderApproverById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSalesOrderApproverById.fulfilled, (state, action) => {
        state.loading = false;
        state.sales_order_approver_detail = action.payload?.data;
      })
      .addCase(getSalesOrderApproverById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Sales Order Approver
      .addCase(createSalesOrderApprover.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSalesOrderApprover.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createSalesOrderApprover.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Sales Order Approver
      .addCase(updateSalesOrderApprover.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSalesOrderApprover.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateSalesOrderApprover.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Sales Order Approver
      .addCase(deleteSalesOrderApprover.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSalesOrderApprover.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteSalesOrderApprover.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setCurrentValues, clearCurrentValues, setActivePayload } = salesOrderApproverSlice.actions;

export default salesOrderApproverSlice.reducer;
